import { useCallback, useEffect, useMemo, useState } from 'react';
import { addDays, eachDayOfInterval, endOfMonth, format, isAfter, isBefore, isEqual, isSameDay, isSameMonth, parseISO, startOfMonth, startOfWeek, } from 'date-fns';
import { formatShortDateWithoutTimezone } from '@ibk/shared-utils';
var ErrorTypeEnum;
(function (ErrorTypeEnum) {
    ErrorTypeEnum["REQUIRED"] = "REQUIRED";
    ErrorTypeEnum["INVALID_DATE"] = "INVALID_DATE";
    ErrorTypeEnum["DATE_BEFORE_MIN"] = "DATE_BEFORE_MIN";
    ErrorTypeEnum["DATE_AFTER_MAX"] = "DATE_AFTER_MAX";
    ErrorTypeEnum["SINGLE_DATE_BEFORE_MIN"] = "SINGLE_DATE_BEFORE_MIN";
    ErrorTypeEnum["SINGLE_DATE_AFTER_MAX"] = "SINGLE_DATE_AFTER_MAX";
})(ErrorTypeEnum || (ErrorTypeEnum = {}));
const ErrorTranslateKey = {
    [ErrorTypeEnum.REQUIRED]: 'selectDate.error.required',
    [ErrorTypeEnum.INVALID_DATE]: 'selectDate.error.invalid-date',
    [ErrorTypeEnum.DATE_BEFORE_MIN]: 'selectDate.error.date-before-min',
    [ErrorTypeEnum.DATE_AFTER_MAX]: 'selectDate.error.date-after-max',
    [ErrorTypeEnum.SINGLE_DATE_BEFORE_MIN]: 'selectDate.error.single-date-before-min',
    [ErrorTypeEnum.SINGLE_DATE_AFTER_MAX]: 'selectDate.error.single-date-after-max',
};
export var ErrorFieldEnum;
(function (ErrorFieldEnum) {
    ErrorFieldEnum["START"] = "START";
    ErrorFieldEnum["END"] = "END";
    ErrorFieldEnum["SINGLE"] = "SINGLE";
})(ErrorFieldEnum || (ErrorFieldEnum = {}));
const SINGLE_SELECTION_TYPE = 'single';
const PERIOD_SELECTION_TYPE = 'period';
export function useSelectDate(params) {
    const { initialDate, minDate, maxDate: maxDateProp, maxDistance, selectionType, select, automaticClosable = true, close, open = () => undefined, currentLanguage = 'pt-BR', readOnly, translate, onClearDate, onClearStartDate, onClearEndDate, } = params;
    const [date, setDate] = useState(undefined);
    const [startDate, setStartDate] = useState(undefined);
    const [endDate, setEndDate] = useState(undefined);
    const [singleText, setSingleText] = useState('');
    const [startText, setStartText] = useState('');
    const [endText, setEndText] = useState('');
    const [singleFocused, setSingleFocused] = useState(false);
    const [startFocused, setStartFocused] = useState(false);
    const [endFocused, setEndFocused] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(() => {
        if (typeof initialDate === 'string')
            return startOfMonth(parseISO(initialDate));
        if (initialDate && typeof initialDate === 'object' && 'start' in initialDate && initialDate.start) {
            return startOfMonth(parseISO(initialDate.start));
        }
        return startOfMonth(new Date());
    });
    const [confirmSelectButtonDisabled, setConfirmSelectButtonDisabled] = useState(false);
    const [errors, setErrors] = useState({});
    const errorObject = useMemo(() => {
        if (!Object.keys(errors).length)
            return [];
        return Object.keys(errors).map(field => ({
            field: field,
            message: translate
                ? translate(ErrorTranslateKey[errors[field].type], errors[field].variables)
                : ErrorTranslateKey[errors[field].type],
            type: errors[field].type,
        }));
    }, [errors, translate]);
    const hasError = useMemo(() => {
        return !!Object.keys(errors).length;
    }, [errors]);
    const addError = (type, field, variables) => {
        setErrors(prev => {
            return { ...prev, [field]: { type, variables } };
        });
    };
    useEffect(() => {
        if (!initialDate)
            return;
        if (selectionType === SINGLE_SELECTION_TYPE && typeof initialDate === 'string') {
            setDate(initialDate);
            setCurrentMonth(startOfMonth(parseISO(initialDate)));
            return;
        }
        if (selectionType === PERIOD_SELECTION_TYPE && typeof initialDate !== 'string') {
            setStartDate(initialDate?.start);
            setEndDate(initialDate?.end);
        }
    }, [initialDate, selectionType]);
    useEffect(() => {
        if (selectionType === SINGLE_SELECTION_TYPE && !automaticClosable) {
            if (!date || initialDate === date) {
                setConfirmSelectButtonDisabled(true);
            }
            else {
                setConfirmSelectButtonDisabled(false);
            }
        }
    }, [initialDate, automaticClosable, date, selectionType]);
    const computedMaxDate = useMemo(() => {
        if (startDate && maxDistance && selectionType === PERIOD_SELECTION_TYPE) {
            const distanceLimit = addDays(parseISO(startDate), maxDistance);
            const distanceLimitIso = toISODate(distanceLimit);
            if (!maxDateProp || isBefore(distanceLimit, parseISO(maxDateProp))) {
                return distanceLimitIso;
            }
        }
        return maxDateProp;
    }, [maxDateProp, maxDistance, selectionType, startDate]);
    const isWithinConstraints = useCallback((iso) => {
        if (minDate && iso < minDate)
            return false;
        if (computedMaxDate && iso > computedMaxDate)
            return false;
        return true;
    }, [minDate, computedMaxDate]);
    const weekdays = useMemo(() => ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'], []);
    const days = useMemo(() => {
        const start = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 0 });
        const end = endOfMonth(currentMonth);
        const lastGridDay = addDays(end, (6 - ((end.getDay() + 7) % 7)) % 7);
        const allDays = eachDayOfInterval({ start, end: lastGridDay });
        const min = minDate ? parseISO(minDate) : undefined;
        const max = computedMaxDate ? parseISO(computedMaxDate) : undefined;
        const isSelectedSingle = (iso) => selectionType === SINGLE_SELECTION_TYPE && date === iso;
        const isInSelectedRange = (iso) => {
            if (selectionType !== PERIOD_SELECTION_TYPE || !startDate)
                return false;
            const d = parseISO(iso);
            const startD = parseISO(startDate);
            const endD = endDate ? parseISO(endDate) : undefined;
            if (!endD)
                return isEqual(d, startD);
            return (isAfter(d, startD) || isEqual(d, startD)) && (isBefore(d, endD) || isEqual(d, endD));
        };
        return allDays.map(d => {
            const iso = toISODate(d);
            const isCurrent = isSameMonth(d, currentMonth);
            let disabled = false;
            if (min && isBefore(d, min))
                disabled = true;
            if (max && isAfter(d, max))
                disabled = true;
            if (selectionType === PERIOD_SELECTION_TYPE && startDate && maxDistance) {
                const limit = addDays(parseISO(startDate), maxDistance);
                if (isAfter(d, limit))
                    disabled = true;
            }
            const selected = isSelectedSingle(iso);
            const inRange = isInSelectedRange(iso);
            const isStart = !!(startDate && isEqual(d, parseISO(startDate)));
            const isEnd = !!(endDate && isSameDay(d, parseISO(endDate)));
            return {
                iso,
                date: d,
                isCurrentMonth: isCurrent,
                isDisabled: disabled,
                isSelected: selected,
                isInRange: inRange,
                isRangeStart: isStart,
                isRangeEnd: isEnd,
            };
        });
    }, [currentMonth, minDate, computedMaxDate, selectionType, date, startDate, endDate, maxDistance]);
    const goToPrevMonth = useCallback(() => {
        setCurrentMonth(prev => addDays(startOfMonth(prev), -1));
    }, []);
    const goToNextMonth = useCallback(() => {
        const end = endOfMonth(currentMonth);
        setCurrentMonth(addDays(end, 1));
    }, [currentMonth]);
    const goToMonth = useCallback((isoDate) => {
        setCurrentMonth(startOfMonth(parseISO(isoDate)));
    }, []);
    const onSelectDay = useCallback((newDayISO) => {
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[ErrorFieldEnum.SINGLE];
            return newErrors;
        });
        setDate(newDayISO);
    }, []);
    const onUpdatePeriod = useCallback((start, end) => {
        select({
            start: start,
            end: end,
        });
        if (!start && !end) {
            setStartDate(undefined);
            setEndDate(undefined);
            setStartText('');
            setEndText('');
        }
        if (automaticClosable) {
            close();
        }
    }, []);
    function onSelectNewDayAfterStartDay(newDay, startDay, newDayISO, startDate) {
        if (isAfter(newDay, startDay)) {
            setEndDate(newDayISO);
            onUpdatePeriod(startDate, newDayISO);
        }
    }
    function onSelectNewDayBeforeStartDay(newDay, startDay, newDayISO) {
        if (isBefore(newDay, startDay)) {
            setStartDate(newDayISO);
            setEndDate(undefined);
        }
    }
    function onSelectNewDayEqualStartDayAndNotEqualEndDay(newDay, startDay, endDay, newDayISO, startDate) {
        if (isEqual(newDay, startDay) && !isEqual(newDay, endDay)) {
            setEndDate(newDayISO);
            onUpdatePeriod(startDate, newDayISO);
        }
    }
    function onSelectNewDayNotEqualStartDayAndEqualEndDay(newDay, startDay, endDay, newDayISO, startDate) {
        if (!isEqual(newDay, startDay) && isEqual(newDay, endDay)) {
            setStartDate(newDayISO);
            onUpdatePeriod(startDate, newDayISO);
        }
    }
    function onSelectNewDayEqualStartDayAndEqualEndDay(newDay, startDay, endDay) {
        if (isEqual(newDay, startDay) && isEqual(newDay, endDay)) {
            setStartDate(undefined);
            setEndDate(undefined);
            onUpdatePeriod(undefined, undefined);
        }
    }
    const onSelectPeriod = useCallback((newDayISO) => {
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[ErrorFieldEnum.START];
            delete newErrors[ErrorFieldEnum.END];
            return newErrors;
        });
        const newDay = parseISO(newDayISO);
        const startDay = startDate ? parseISO(startDate) : undefined;
        const endDay = endDate ? parseISO(endDate) : undefined;
        if (startDate && endDate && startDay && endDay) {
            onSelectNewDayAfterStartDay(newDay, startDay, newDayISO, startDate);
            onSelectNewDayBeforeStartDay(newDay, startDay, newDayISO);
            onSelectNewDayEqualStartDayAndNotEqualEndDay(newDay, startDay, endDay, newDayISO, startDate);
            onSelectNewDayNotEqualStartDayAndEqualEndDay(newDay, startDay, endDay, newDayISO, startDate);
            onSelectNewDayEqualStartDayAndEqualEndDay(newDay, startDay, endDay);
        }
        if (startDate && !endDate && startDay && !isBefore(newDay, startDay)) {
            setEndDate(newDayISO);
            onUpdatePeriod(startDate, newDayISO);
        }
        if (startDate && !endDate && startDay && isBefore(newDay, startDay)) {
            setStartDate(newDayISO);
        }
        if (!startDate) {
            setStartDate(newDayISO);
        }
    }, [startDate, endDate]);
    const onDayClick = useCallback((isoDate) => {
        if (selectionType === SINGLE_SELECTION_TYPE) {
            onSelectDay(isoDate);
            if (automaticClosable) {
                select(isoDate);
                close();
            }
        }
        if (selectionType === PERIOD_SELECTION_TYPE) {
            onSelectPeriod(isoDate);
        }
    }, [onSelectDay, onSelectPeriod, selectionType, automaticClosable, close, select]);
    const setStartDateFromInput = useCallback((isoDate) => {
        if (minDate && isoDate < minDate) {
            addError(ErrorTypeEnum.DATE_BEFORE_MIN, ErrorFieldEnum.START, {
                minDate: formatShortDateWithoutTimezone(minDate, currentLanguage),
            });
            setStartDate(undefined);
            setEndDate(undefined);
            return false;
        }
        if (maxDateProp && isoDate > maxDateProp) {
            addError(ErrorTypeEnum.DATE_AFTER_MAX, ErrorFieldEnum.START, {
                maxDate: formatShortDateWithoutTimezone(maxDateProp, currentLanguage),
            });
            setStartDate(undefined);
            setEndDate(undefined);
            return false;
        }
        setStartDate(isoDate);
        setEndDate(undefined);
        return true;
    }, [minDate, maxDateProp, currentLanguage]);
    const setEndDateFromInput = useCallback((isoDate) => {
        if (minDate && isoDate < minDate) {
            addError(ErrorTypeEnum.DATE_BEFORE_MIN, ErrorFieldEnum.END, {
                minDate: formatShortDateWithoutTimezone(minDate, currentLanguage),
            });
            setEndDate(undefined);
            return false;
        }
        if (startDate && isoDate < startDate) {
            addError(ErrorTypeEnum.DATE_BEFORE_MIN, ErrorFieldEnum.END, {
                minDate: formatShortDateWithoutTimezone(startDate, currentLanguage),
            });
            setEndDate(undefined);
            return false;
        }
        if (computedMaxDate && isoDate > computedMaxDate) {
            addError(ErrorTypeEnum.DATE_AFTER_MAX, ErrorFieldEnum.END, {
                maxDate: formatShortDateWithoutTimezone(computedMaxDate, currentLanguage),
            });
            setEndDate(undefined);
            return false;
        }
        setEndDate(isoDate);
        return true;
    }, [minDate, startDate, computedMaxDate, currentLanguage]);
    const clearStartDate = useCallback(() => {
        setStartDate(undefined);
        setEndDate(undefined);
        onClearStartDate?.();
    }, [onClearStartDate]);
    const clearEndDate = useCallback(() => {
        setEndDate(undefined);
        onClearEndDate?.();
    }, [onClearEndDate]);
    const setSingleDateFromInput = useCallback((isoDate) => {
        if (minDate && isoDate < minDate) {
            addError(ErrorTypeEnum.SINGLE_DATE_BEFORE_MIN, ErrorFieldEnum.SINGLE, {
                minDate: formatShortDateWithoutTimezone(minDate, currentLanguage),
            });
            setDate(undefined);
            return false;
        }
        if (maxDateProp && isoDate > maxDateProp) {
            addError(ErrorTypeEnum.SINGLE_DATE_AFTER_MAX, ErrorFieldEnum.SINGLE, {
                maxDate: formatShortDateWithoutTimezone(maxDateProp, currentLanguage),
            });
            setDate(undefined);
            return false;
        }
        setDate(isoDate);
        return true;
    }, [minDate, maxDateProp, currentLanguage]);
    const clearDate = useCallback(() => {
        setDate(undefined);
        onClearDate?.();
    }, [onClearDate]);
    const singleInputValue = useMemo(() => {
        if (singleFocused)
            return maskDigitsToLocaleString(singleText, currentLanguage);
        return date ? formatShortDateWithoutTimezone(date, currentLanguage) : '';
    }, [singleFocused, singleText, currentLanguage, date]);
    const startInputValue = useMemo(() => {
        if (startFocused)
            return maskDigitsToLocaleString(startText, currentLanguage);
        return startDate ? formatShortDateWithoutTimezone(startDate, currentLanguage) : '';
    }, [startFocused, startText, currentLanguage, startDate]);
    const endInputValue = useMemo(() => {
        if (endFocused)
            return maskDigitsToLocaleString(endText, currentLanguage);
        return endDate ? formatShortDateWithoutTimezone(endDate, currentLanguage) : '';
    }, [endFocused, endText, currentLanguage, endDate]);
    const onSingleFocus = useCallback(() => {
        setSingleFocused(true);
        setSingleText(prev => (prev && prev.length > 0 ? prev : isoToDigits(date, currentLanguage)));
        if (!readOnly)
            open();
    }, [date, currentLanguage, readOnly, open]);
    const onSingleBlur = useCallback(() => {
        setSingleFocused(false);
        setSingleText('');
    }, []);
    const onSingleChange = useCallback((e) => {
        const digits = e.target.value.replace(/\D/g, '');
        setSingleText(digits);
        const iso = toIsoFromDigits(digits, currentLanguage);
        if (iso && isValidIso(iso)) {
            const isValidRange = setSingleDateFromInput(iso);
            if (isValidRange) {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[ErrorFieldEnum.SINGLE];
                    return newErrors;
                });
                select(iso);
                if (isWithinConstraints(iso)) {
                    goToMonth(iso);
                }
            }
        }
        else if (digits.length < 8) {
            clearDate();
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[ErrorFieldEnum.SINGLE];
                return newErrors;
            });
        }
        else if (digits.length === 8) {
            addError(ErrorTypeEnum.INVALID_DATE, ErrorFieldEnum.SINGLE);
            clearDate();
        }
    }, [currentLanguage, setSingleDateFromInput, isWithinConstraints, goToMonth, clearDate, select]);
    const onStartFocus = useCallback(() => {
        setStartFocused(true);
        setStartText(prev => (prev && prev.length > 0 ? prev : isoToDigits(startDate, currentLanguage)));
        if (!readOnly)
            open();
    }, [startDate, currentLanguage, readOnly, open]);
    const onStartBlur = useCallback(() => {
        setStartFocused(false);
        setStartText('');
    }, []);
    const onStartChange = useCallback((e) => {
        const digits = e.target.value.replace(/\D/g, '');
        setStartText(digits);
        const iso = toIsoFromDigits(digits, currentLanguage);
        if (iso && isValidIso(iso)) {
            const isValidRange = setStartDateFromInput(iso);
            if (isValidRange) {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[ErrorFieldEnum.START];
                    return newErrors;
                });
                if (isWithinConstraints(iso)) {
                    goToMonth(iso);
                }
                clearEndDate();
            }
            return;
        }
        if (digits.length < 8) {
            clearStartDate();
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[ErrorFieldEnum.START];
                return newErrors;
            });
        }
        else if (digits.length === 8) {
            clearStartDate();
            addError(ErrorTypeEnum.INVALID_DATE, ErrorFieldEnum.START);
        }
    }, [currentLanguage, setStartDateFromInput, isWithinConstraints, goToMonth, clearStartDate]);
    const onEndFocus = useCallback(() => {
        setEndFocused(true);
        setEndText(prev => (prev && prev.length > 0 ? prev : isoToDigits(endDate, currentLanguage)));
        if (!readOnly)
            open();
    }, [endDate, currentLanguage, readOnly, open]);
    const onEndBlur = useCallback(() => {
        setEndFocused(false);
        setEndText('');
    }, []);
    const onEndChange = useCallback((e) => {
        const digits = e.target.value.replace(/\D/g, '');
        setEndText(digits);
        const iso = toIsoFromDigits(digits, currentLanguage);
        if (iso && isValidIso(iso)) {
            const isValidRange = setEndDateFromInput(iso);
            if (isValidRange) {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[ErrorFieldEnum.END];
                    return newErrors;
                });
                if ((!startDate || iso >= startDate) && isWithinConstraints(iso)) {
                    goToMonth(iso);
                    if (startDate) {
                        onUpdatePeriod(startDate, iso);
                    }
                }
            }
            return;
        }
        if (digits.length < 8) {
            clearEndDate();
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[ErrorFieldEnum.END];
                return newErrors;
            });
        }
        else if (digits.length === 8) {
            addError(ErrorTypeEnum.INVALID_DATE, ErrorFieldEnum.END);
            clearEndDate();
        }
    }, [currentLanguage, setEndDateFromInput, startDate, isWithinConstraints, goToMonth, clearEndDate]);
    function toISODate(date) {
        return format(date, 'yyyy-MM-dd');
    }
    function isoToDigits(iso, locale = 'pt-BR') {
        if (!iso)
            return '';
        const y = iso.substring(0, 4);
        const m = iso.substring(5, 7);
        const d = iso.substring(8, 10);
        if (locale === 'en-US')
            return `${m}${d}${y}`;
        return `${d}${m}${y}`;
    }
    function maskDigitsToLocaleString(digits, locale = 'pt-BR') {
        const only = digits.replace(/\D/g, '').slice(0, 8);
        if (only.length === 0)
            return '';
        if (locale === 'en-US') {
            const mm = only.slice(0, 2);
            const dd = only.slice(2, 4);
            const yyyy = only.slice(4, 8);
            if (only.length <= 2)
                return mm;
            if (only.length <= 4)
                return `${mm}/${dd}`;
            return `${mm}/${dd}/${yyyy}`;
        }
        const dd = only.slice(0, 2);
        const mm = only.slice(2, 4);
        const yyyy = only.slice(4, 8);
        if (only.length <= 2)
            return dd;
        if (only.length <= 4)
            return `${dd}/${mm}`;
        return `${dd}/${mm}/${yyyy}`;
    }
    function toIsoFromDigits(digits, locale = 'pt-BR') {
        const only = digits.replace(/\D/g, '');
        if (only.length < 8)
            return undefined;
        let dd, mm, yyyy;
        if (locale === 'en-US') {
            mm = only.slice(0, 2);
            dd = only.slice(2, 4);
            yyyy = only.slice(4, 8);
        }
        else {
            dd = only.slice(0, 2);
            mm = only.slice(2, 4);
            yyyy = only.slice(4, 8);
        }
        const day = Number(dd);
        const month = Number(mm);
        if (!(day >= 1 && day <= 31))
            return undefined;
        if (!(month >= 1 && month <= 12))
            return undefined;
        return `${yyyy}-${mm}-${dd}`;
    }
    function isValidIso(iso) {
        const d = parseISO(iso);
        return !Number.isNaN(d.getTime());
    }
    function onConfirmSelect() {
        select(date);
        close();
    }
    return {
        date,
        startDate,
        endDate,
        currentMonth,
        weekdays,
        days,
        goToPrevMonth,
        goToNextMonth,
        goToMonth,
        onDayClick,
        minDate,
        maxDate: computedMaxDate,
        isWithinConstraints,
        setStartDateFromInput,
        setEndDateFromInput,
        clearStartDate,
        clearEndDate,
        setSingleDateFromInput,
        clearDate,
        singleInputValue,
        onSingleFocus,
        onSingleBlur,
        onSingleChange,
        startInputValue,
        onStartFocus,
        onStartBlur,
        onStartChange,
        endInputValue,
        onEndFocus,
        onEndBlur,
        onEndChange,
        onConfirmSelect,
        confirmSelectButtonDisabled,
        error: errorObject,
        hasError,
    };
}
